import Phaser from 'phaser';
import { AudioManager } from '../systems/AudioManager';

export type ButtonStyle = 'primary' | 'secondary' | 'gold';

const STYLES: Record<ButtonStyle, { bg: number; bgHover: number; border: number; color: string }> = {
  primary:   { bg: 0xff6fa1, bgHover: 0xe54e87, border: 0xe54e87, color: '#ffffff' },
  secondary: { bg: 0xfff5f8, bgHover: 0xffd6e6, border: 0xffd6e6, color: '#3a2540' },
  gold:      { bg: 0xffc94c, bgHover: 0xf0a020, border: 0xf0a020, color: '#ffffff' },
};

export class Button extends Phaser.GameObjects.Container {
  private bgGraphics: Phaser.GameObjects.Graphics;
  private label: Phaser.GameObjects.Text;
  private style: ButtonStyle;
  private btnW: number;
  private btnH: number;

  constructor(
    scene: Phaser.Scene,
    x: number, y: number,
    text: string,
    onClick: () => void,
    options: { width?: number; height?: number; style?: ButtonStyle } = {}
  ) {
    super(scene, x, y);
    this.style = options.style ?? 'primary';
    const s = STYLES[this.style];
    this.btnW = options.width ?? 160;
    this.btnH = options.height ?? 48;

    // Rounded background via Graphics
    this.bgGraphics = scene.add.graphics();
    this._drawBg(s.bg, s.border);

    this.label = scene.add.text(0, 0, text, {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '16px',
      fontStyle: '700',
      color: s.color,
    }).setOrigin(0.5);

    // Invisible hit zone
    const hit = scene.add.rectangle(0, 0, this.btnW, this.btnH, 0x000000, 0);
    hit.setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => { this._drawBg(s.bgHover, s.border); });
    hit.on('pointerout', () => { this._drawBg(s.bg, s.border); });
    hit.on('pointerdown', () => { AudioManager.playSfx('click'); onClick(); });

    this.add([this.bgGraphics, hit, this.label]);
    scene.add.existing(this);
  }

  private _drawBg(fillColor: number, borderColor: number): void {
    const g = this.bgGraphics;
    g.clear();
    g.fillStyle(fillColor, 1);
    g.fillRoundedRect(-this.btnW / 2, -this.btnH / 2, this.btnW, this.btnH, 12);
    g.lineStyle(1.5, borderColor, 0.6);
    g.strokeRoundedRect(-this.btnW / 2, -this.btnH / 2, this.btnW, this.btnH, 12);
  }

  setText(text: string): this {
    this.label.setText(text);
    return this;
  }
}
